/**
 * Favorites on the TV.
 *
 * Only favorites, unlike the web page's two tabs. History exists to answer "what was that
 * thing called" from a desk; on a television it is a list you scroll past on the way to
 * the one you actually meant, and Continue Watching on the home screen already covers the
 * case that matters — picking up where you left off.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { BrowsableType, FavoriteDto } from '@filmodasi/shared';
import { useApi } from '../../../api/hooks';
import { Spinner, ErrorBox, Empty } from '../../../components/ui';
import { detailPath } from '../../../catalog/detailPath';
import { TvCard } from '../TvShell';
import { useFocusable } from '../focus/useFocusable';

const FILTERS: { value: BrowsableType | ''; label: string }[] = [
  { value: '', label: 'Hepsi' },
  { value: 'live', label: 'Canlı' },
  { value: 'movie', label: 'Film' },
  { value: 'series', label: 'Dizi' },
];

function Chip({
  id,
  label,
  active,
  onSelect,
}: {
  id: string;
  label: string;
  active: boolean;
  onSelect: () => void;
}) {
  const { ref, focused } = useFocusable<HTMLButtonElement>({ id, group: 'fav:filter', onSelect });
  return (
    <button
      ref={ref}
      type="button"
      className={`tvchip${focused ? ' is-focused' : ''}${active ? ' is-active' : ''}`}
      onClick={onSelect}
    >
      {label}
    </button>
  );
}

export function TvMyListPage() {
  const navigate = useNavigate();
  const [type, setType] = useState<BrowsableType | ''>('');
  const favorites = useApi<FavoriteDto[]>(`/api/favorites${type ? `?type=${type}` : ''}`);

  return (
    <>
      <h1 className="tv__heading">Listem</h1>

      <div className="tvchips">
        {FILTERS.map((f) => (
          <Chip
            key={f.value || 'all'}
            id={`fav:filter:${f.value || 'all'}`}
            label={f.label}
            active={type === f.value}
            onSelect={() => setType(f.value)}
          />
        ))}
      </div>

      {favorites.error && <ErrorBox message={favorites.error} onRetry={favorites.reload} />}
      {favorites.loading && !favorites.data && <Spinner />}

      {favorites.data && favorites.data.length === 0 && (
        <Empty>Burada henüz bir şey yok. Bir film ya da dizide kalbe basın.</Empty>
      )}

      <div className="tvgrid">
        {favorites.data?.map((fav, i) => (
          <TvCard
            key={fav.id}
            id={`fav:${i}`}
            group="fav:grid"
            title={fav.item.title}
            subtitle={fav.item.year ? String(fav.item.year) : null}
            art={fav.item.poster ?? fav.item.logo}
            poster={fav.type !== 'live'}
            onSelect={() => navigate(detailPath(fav.item))}
          />
        ))}
      </div>
    </>
  );
}
